// =============================================================================
// PortalHome — /portal — the personal dashboard
// =============================================================================
// Every employee lands here. It is the only screen most of the crew ever open,
// so it is the one that has to be good.
//
// WHAT CHANGED, AND WHY
// ---------------------
// The old version was four flat counters and a grid of identical link cards.
// Everything was there and none of it was legible: "Licences due soon: 2" tells
// you a number but not which two, not when, and not whether it matters today.
// James: "even the equipment and upcoming inspections, all of it is there but
// it's not very user friendly and no visual aids."
//
// So it is rebuilt around one question, asked in the person's own words:
//
//   AM I RIGHT FOR SITE?
//
// then, immediately underneath, WHAT NEEDS DOING - named, dated, and sorted
// worst first, pulling from tickets, vehicles, power tools and rigging gear
// into a single list. A person should not have to visit four screens to find
// out that something of theirs has lapsed.
//
// SEPARATE FROM HR, BUT SPEAKING THE SAME LANGUAGE
// ------------------------------------------------
// James: "let's not get field into HR portal, best to keep it separate but
// mirroring in personal portal."
//
// Nothing here links into /hr/*. But the readiness bar, the five statuses, their
// colours and their words all come from complianceShared - the same module the
// compliance matrix uses. A rigger looking at his own portal and the director
// looking at the matrix see the same figure, drawn the same way, meaning the
// same thing. That is the point: if they disagreed, the disagreement would
// happen at a site gate.
//
// The data is derived, never stored. v_employee_compliance_summary and
// v_employee_requirements are security_invoker, so a field user selecting from
// them gets their own rows and nobody else's - there is no permission check in
// this file because there does not need to be one.
// =============================================================================

import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, LayoutDashboard, Clock, CalendarDays, FileCheck2, Truck,
  UserCircle, ArrowRight, Receipt, ClipboardList, UserCog, ShieldCheck,
  Wrench, AlertTriangle, CheckCircle2, Sun, Sunrise, Moon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import type { Role } from "@/lib/auth";
import { Card, Spinner } from "@/components/ui";
import { fmtDate } from "@/lib/dates";
import { MyScheduleSnapshot } from "@/features/schedule/MyHorizonScreen";
import {
  ReadinessBar, ReadinessLegend, ReadinessRing, readiness, bestStatus, summariseRequirements,
  STATUS_META, type ReqStatus, type StatusCounts, type ReadinessBand,
} from "@/features/compliance/complianceShared";

// -----------------------------------------------------------------------------
// The verdict tints the whole hero card. Written out as literal class strings
// because Tailwind scans source text - a class built from a variable at runtime
// never makes it into the stylesheet.
// -----------------------------------------------------------------------------
const BAND_WASH: Record<ReadinessBand, string> = {
  ready:     "from-ok/10 via-ok/[0.03]",
  renewals:  "from-warn/10 via-warn/[0.03]",
  gaps:      "from-warn/10 via-warn/[0.03]",
  not_ready: "from-danger/10 via-danger/[0.03]",
  unset:     "from-text/[0.06] via-transparent",
};

const BAND_RULE: Record<ReadinessBand, string> = {
  ready: "bg-ok", renewals: "bg-warn", gaps: "bg-warn",
  not_ready: "bg-danger", unset: "bg-text-dim/40",
};

// Monday-start week, because that is how a timesheet week runs.
function weekStart(): Date {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  d.setHours(0, 0, 0, 0);
  return d;
}

function greeting(): { text: string; icon: LucideIcon } {
  const h = new Date().getHours();
  if (h < 12) return { text: "Good morning",   icon: Sunrise };
  if (h < 17) return { text: "Good afternoon", icon: Sun };
  return { text: "Good evening", icon: Moon };
}

// -----------------------------------------------------------------------------
// Dates. Everything here is a plain yyyy-mm-dd string comparison, which is safe
// because ISO dates sort lexically and avoids dragging timezones into a screen
// that gets read at 6am on a phone.
// -----------------------------------------------------------------------------
const iso = (d: Date) => d.toISOString().slice(0, 10);
const today = () => iso(new Date());
const inDays = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return iso(d);
};

function daysUntil(date: string): number {
  const ms = new Date(date + "T00:00:00").getTime() - new Date(today() + "T00:00:00").getTime();
  return Math.round(ms / 86400000);
}

// How a due date reads to a person, rather than to a database.
function whenLabel(date: string): string {
  const n = daysUntil(date);
  if (n < 0)  return `${Math.abs(n)} day${Math.abs(n) === 1 ? "" : "s"} ago`;
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  if (n <= 21) return `in ${n} days`;
  return fmtDate(date);
}

// -----------------------------------------------------------------------------
interface ItemDef {
  label: string; description: string; to: string; icon: LucideIcon; roles?: Role[];
}

// Subbies don't use Coretel's timesheet, leave, receipts or equipment systems.
// What they do need: their inductions stay current, their licences are visible
// if Coretel uploads them, and their contact details.
const PORTAL_ITEMS: ItemDef[] = [
  { label: "My timesheet",  description: "Hours this week, submit, approve.",              to: "/timesheets",  icon: Clock,        roles: ["director","manager","supervisor","field"] },
  { label: "My schedule",   description: "Today, upcoming sites, breaks — your horizon.",  to: "/my-schedule", icon: CalendarDays, roles: ["director","manager","supervisor","field"] },
  { label: "My leave",      description: "Request and track balance.",                     to: "/leave",       icon: CalendarDays, roles: ["director","manager","supervisor","field"] },
  { label: "My licences",   description: "Your tickets, expiries, uploads.",               to: "/licences",    icon: FileCheck2 },
  { label: "My inductions", description: "Carrier + tier-1 contractor inductions.",        to: "/inductions",  icon: ShieldCheck },
  { label: "My receipts",   description: "Snap receipts on the road, director codes.",     to: "/receipts",    icon: Receipt,      roles: ["director","manager","supervisor","field"] },
  { label: "My equipment",  description: "Assets allocated to you.",                       to: "/equipment",   icon: Truck,        roles: ["director","manager","supervisor","field"] },
  { label: "Inspections",   description: "Rigging colour-tag + Test & Tag register.",      to: "/inspections", icon: Wrench,       roles: ["director","manager","supervisor","field"] },
  { label: "My profile",    description: "Contact, bank, super, preferences.",             to: "/profile",     icon: UserCircle },
  { label: "User admin",    description: "Invite users, set roles, deactivate accounts.",  to: "/admin/users", icon: UserCog,      roles: ["director"] },
];

interface PrimaryActionDef { label: string; to: string; icon: LucideIcon; roles: Role[] }

const PRIMARY_ACTIONS: PrimaryActionDef[] = [
  { label: "Log hours",   to: "/timesheets",   icon: Clock,         roles: ["field","supervisor"] },
  { label: "Log receipt", to: "/receipts/new", icon: Receipt,       roles: ["field","supervisor"] },
  { label: "HOP photo",   to: "/acceptance",   icon: ClipboardList, roles: ["field","supervisor"] },
];

// -----------------------------------------------------------------------------
// One thing that needs doing. Tickets and gear both end up in this shape, which
// is the whole trick: a lapsed rigging inspection and a lapsed ticket are the
// same kind of problem to the person who has to deal with them.
// -----------------------------------------------------------------------------
interface Action {
  key: string;
  what: string;          // "Working at Heights"
  detail: string;        // "expired 12 days ago"
  date: string | null;
  severity: 0 | 1 | 2;   // 0 overdue, 1 due soon, 2 attention
  to: string;
  icon: LucideIcon;
}

export default function PortalHome() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const employeeId = user?.employee_id ?? null;

  // ---------------------------------------------------------------------------
  // Compliance — the same view the matrix reads, filtered to this person by RLS.
  // ---------------------------------------------------------------------------
  const { data: reqs = [], isLoading: loadingReqs } = useQuery({
    queryKey: ["portal_requirements", employeeId],
    enabled: !!employeeId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_employee_requirements")
        .select("role_id, role_name, alt_group, is_mandatory, licence_type_id, licence_name, licence_id, expiry_date, status")
        .eq("employee_id", employeeId!);
      if (error) throw error;
      return (data ?? []) as unknown as {
        role_id: string; role_name: string; alt_group: string; is_mandatory: boolean;
        licence_type_id: string; licence_name: string; licence_id: string | null;
        expiry_date: string | null; status: ReqStatus;
      }[];
    },
  });

  // ---------------------------------------------------------------------------
  // Gear assigned to this person, with whatever is due on it.
  // ---------------------------------------------------------------------------
  const { data: gear, isLoading: loadingGear } = useQuery({
    queryKey: ["portal_gear", employeeId],
    enabled: !!employeeId,
    queryFn: async () => {
      const [v, p, r] = await Promise.all([
        supabase.from("vehicles")
          .select("vehicle_id, vehicle_code, rego, make, model, rego_expiry, service_due_date")
          .eq("assigned_to_employee_id", employeeId!).eq("is_active", true),
        supabase.from("power_tools")
          .select("tool_id, tool_code, display_name, next_test_and_tag_due, inspection_required")
          .eq("assigned_to_employee_id", employeeId!).eq("is_active", true),
        supabase.from("rigging_equipment")
          .select("rigging_id, rigging_code, display_name, next_inspection_date, inspection_required, tag_colour")
          .eq("assigned_to_employee_id", employeeId!).eq("status", "in_service"),
      ]);
      return {
        vehicles: (v.data ?? []) as any[],
        tools:    (p.data ?? []) as any[],
        rigging:  (r.data ?? []) as any[],
      };
    },
  });

  // ---------------------------------------------------------------------------
  // This week's hours.
  //
  // Replaces the leave-balance card. James: "change timesheets with my leave,
  // they don't use that much - we even have a pending open ticket to track and
  // record the leave balance from the award." A balance that reads "No balance
  // recorded yet" for everybody is worse than no card at all, and hours are the
  // thing the crew touch every single day.
  // ---------------------------------------------------------------------------
  const { data: week } = useQuery({
    queryKey: ["portal_week", employeeId],
    enabled: !!employeeId,
    queryFn: async () => {
      const start = weekStart();
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      const { data } = await supabase
        .from("timesheet_entries")
        .select("work_date, total_hours, submitted_at, approved_at")
        .eq("employee_id", employeeId!)
        .gte("work_date", iso(start))
        .lte("work_date", iso(end));
      return (data ?? []) as {
        work_date: string; total_hours: number;
        submitted_at: string | null; approved_at: string | null;
      }[];
    },
  });

  // ---------------------------------------------------------------------------
  // Resolve requirements the same way every other compliance screen does:
  // alternatives are OR'd, and the same ticket wanted by two roles is one thing.
  // ---------------------------------------------------------------------------
  const compliance = useMemo(() => summariseRequirements(reqs), [reqs]);

  // ---------------------------------------------------------------------------
  // Everything that needs doing, from every source, in one list.
  // ---------------------------------------------------------------------------
  const actions = useMemo<Action[]>(() => {
    const out: Action[] = [];
    const soon = inDays(60);
    const gearSoon = inDays(30);
    const now = today();

    // --- tickets ---
    for (const g of compliance.list) {
      if (!g.mand) continue;
      const name = g.names.join(" or ");
      if (g.status === "expired") {
        out.push({
          key: `t:${name}`, what: name,
          detail: g.expiry ? `expired ${whenLabel(g.expiry)}` : "expired",
          date: g.expiry, severity: 0, to: "/licences", icon: FileCheck2,
        });
      } else if (g.status === "missing") {
        out.push({
          key: `t:${name}`, what: name, detail: "nothing on file",
          date: null, severity: 2, to: "/licences", icon: FileCheck2,
        });
      } else if (g.status === "expiring" && g.expiry && g.expiry <= soon) {
        out.push({
          key: `t:${name}`, what: name, detail: `expires ${whenLabel(g.expiry)}`,
          date: g.expiry, severity: 1, to: "/licences", icon: FileCheck2,
        });
      } else if (g.status === "pending") {
        out.push({
          key: `t:${name}`, what: name, detail: "uploaded, waiting to be checked",
          date: null, severity: 2, to: "/licences", icon: FileCheck2,
        });
      }
    }

    // --- vehicles ---
    for (const v of gear?.vehicles ?? []) {
      const label = `${v.rego ?? v.vehicle_code}${v.make ? ` — ${v.make} ${v.model ?? ""}`.trimEnd() : ""}`;
      if (v.rego_expiry && v.rego_expiry <= gearSoon) {
        out.push({
          key: `v:rego:${v.vehicle_id}`, what: `${label} registration`,
          detail: v.rego_expiry < now ? `expired ${whenLabel(v.rego_expiry)}` : `due ${whenLabel(v.rego_expiry)}`,
          date: v.rego_expiry, severity: v.rego_expiry < now ? 0 : 1,
          to: "/equipment", icon: Truck,
        });
      }
      if (v.service_due_date && v.service_due_date <= gearSoon) {
        out.push({
          key: `v:svc:${v.vehicle_id}`, what: `${label} service`,
          detail: v.service_due_date < now ? `overdue ${whenLabel(v.service_due_date)}` : `due ${whenLabel(v.service_due_date)}`,
          date: v.service_due_date, severity: v.service_due_date < now ? 0 : 1,
          to: "/equipment", icon: Truck,
        });
      }
    }

    // --- power tools: test and tag ---
    for (const t of gear?.tools ?? []) {
      if (!t.inspection_required || !t.next_test_and_tag_due) continue;
      if (t.next_test_and_tag_due > gearSoon) continue;
      out.push({
        key: `p:${t.tool_id}`, what: `${t.display_name} — test & tag`,
        detail: t.next_test_and_tag_due < now
          ? `overdue ${whenLabel(t.next_test_and_tag_due)}`
          : `due ${whenLabel(t.next_test_and_tag_due)}`,
        date: t.next_test_and_tag_due,
        severity: t.next_test_and_tag_due < now ? 0 : 1,
        to: "/inspections", icon: Wrench,
      });
    }

    // --- rigging gear: colour-tag inspection ---
    for (const r of gear?.rigging ?? []) {
      if (!r.inspection_required || !r.next_inspection_date) continue;
      if (r.next_inspection_date > gearSoon) continue;
      out.push({
        key: `r:${r.rigging_id}`, what: `${r.display_name} — inspection`,
        detail: r.next_inspection_date < now
          ? `overdue ${whenLabel(r.next_inspection_date)}`
          : `due ${whenLabel(r.next_inspection_date)}`,
        date: r.next_inspection_date,
        severity: r.next_inspection_date < now ? 0 : 1,
        to: "/inspections", icon: ShieldCheck,
      });
    }

    // Worst first, then soonest. Something already overdue outranks something
    // due next week no matter what the dates say.
    return out.sort((a, b) =>
      a.severity - b.severity
      || (a.date ?? "9999").localeCompare(b.date ?? "9999")
      || a.what.localeCompare(b.what));
  }, [compliance.list, gear]);

  const gearCount =
    (gear?.vehicles.length ?? 0) + (gear?.tools.length ?? 0) + (gear?.rigging.length ?? 0);

  // Mon-Sun, so the shape of somebody's week is visible at a glance rather than
  // hidden inside a single total.
  const weekDays = useMemo(() => {
    const start = weekStart();
    const labels = ["M", "T", "W", "T", "F", "S", "S"];
    const days = labels.map((label, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return { label, date: iso(d), hours: 0 };
    });
    for (const e of week ?? []) {
      const day = days.find(d => d.date === e.work_date);
      if (day) day.hours += Number(e.total_hours ?? 0);
    }
    return days;
  }, [week]);

  const weekTotal    = weekDays.reduce((n, d) => n + d.hours, 0);
  const unsubmitted  = (week ?? []).filter(e => !e.submitted_at).length;
  const todayIso     = today();

  const loading = loadingReqs || loadingGear;

  if (!user) return null;
  const firstName = user.first_name ?? "there";
  const Verdict = compliance.verdict.icon;
  const hi = greeting();
  const Hi = hi.icon;
  const band = compliance.verdict.band;

  // The single most urgent thing, named. A percentage tells you there is a
  // problem; this tells you which one to deal with first.
  const nextUp = actions[0];

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto pb-20">
      <button
        onClick={() => navigate("/")}
        className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3"
        type="button"
      >
        <ArrowLeft size={14} /> All hubs
      </button>

      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-gradient-to-br from-accent/20 to-accent/5 text-accent flex items-center justify-center border border-accent/30">
            <Hi size={22} />
          </div>
          <div>
            <h1 className="text-[22px] md:text-[26px] font-semibold text-text leading-tight">
              {hi.text}, {firstName}
            </h1>
            <p className="text-[12.5px] text-text-muted mt-0.5">
              {new Date().toLocaleDateString("en-AU", {
                weekday: "long", day: "numeric", month: "long",
              })}
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : (
        <>
          {/* ================================================================
              AM I RIGHT FOR SITE — the headline
              ----------------------------------------------------------------
              Number, word and icon together, never colour alone. The bar is a
              glance; the list below carries the detail. Same component, same
              tokens and same five statuses the compliance matrix uses, so the
              figure a rigger sees here is the figure the director sees there.
          ================================================================= */}
          <Card className={`mb-4 overflow-hidden bg-gradient-to-br ${BAND_WASH[band]} to-transparent`}>
            {/* A 2px rule in the verdict colour across the top. Colour is never
                doing the work alone - the word sits right underneath it - but it
                is what makes the card readable from arm's length on a phone. */}
            <div className={`h-[3px] w-full ${BAND_RULE[band]}`} />

            <div className="p-5 md:p-6">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono">
                  Am I right for site
                </div>
                <Link to="/my-compliance"
                      className="inline-flex items-center gap-1.5 text-[12px] text-accent hover:text-accent-bright font-medium">
                  See what I need <ArrowRight size={14} />
                </Link>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <div className="flex-shrink-0 self-center sm:self-auto">
                  <ReadinessRing pct={compliance.pct} colorClass={compliance.verdict.text} />
                </div>

                <div className="min-w-0 flex-1">
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded ${compliance.verdict.chip} ${compliance.verdict.text} text-[13px] font-medium`}>
                    <Verdict size={14} /> {compliance.verdict.label}
                  </div>

                  <div className="text-[12.5px] text-text-muted mt-2">
                    {compliance.required === 0
                      ? "No requirements set for you yet — ask the office."
                      : <>{compliance.met} of {compliance.required} tickets current
                          {(compliance.counts.expiring ?? 0) > 0 &&
                            <> · {compliance.counts.expiring} running out soon</>}</>}
                  </div>

                  {compliance.required > 0 && (
                    <>
                      <div className="mt-4">
                        <ReadinessBar counts={compliance.counts} height={12} />
                      </div>
                      <div className="mt-2.5">
                        <ReadinessLegend counts={compliance.counts} />
                      </div>
                    </>
                  )}

                  {nextUp && (
                    <div className="mt-3 text-[12px] text-text">
                      <span className="text-text-muted">First thing to sort: </span>
                      <span className="font-medium">{nextUp.what}</span>
                      <span className="text-text-muted"> — {nextUp.detail}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* ================================================================
              NEEDS YOU — tickets and gear in one list
              ----------------------------------------------------------------
              The old portal made you visit four screens to discover that
              something of yours had lapsed. Anything overdue or due inside a
              month lands here, whatever it is, worst first.
          ================================================================= */}
          {actions.length > 0 ? (
            <Card className="mb-6">
              <div className="p-5">
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={16} className="text-warn" />
                    <h2 className="text-[14px] font-semibold text-text">Needs you</h2>
                  </div>
                  {/* Counts every severity. The first version reported "0 overdue,
                      0 coming up" above a list of seventeen missing tickets,
                      because nothing missing has a date. */}
                  <span className="text-[11px] text-text-muted">
                    {[
                      actions.filter(a => a.severity === 0).length &&
                        `${actions.filter(a => a.severity === 0).length} overdue`,
                      actions.filter(a => a.severity === 1).length &&
                        `${actions.filter(a => a.severity === 1).length} coming up`,
                      actions.filter(a => a.severity === 2).length &&
                        `${actions.filter(a => a.severity === 2).length} to sort out`,
                    ].filter(Boolean).join(" · ")}
                  </span>
                </div>

                <div className="border border-line rounded-md divide-y divide-line overflow-hidden">
                  {actions.slice(0, 7).map(a => {
                    const Icon = a.icon;
                    const tone =
                      a.severity === 0 ? { dot: "bg-danger", text: "text-danger" } :
                      a.severity === 1 ? { dot: "bg-warn",   text: "text-warn" } :
                                         { dot: "bg-text-dim/40", text: "text-text-muted" };
                    return (
                      <Link key={a.key} to={a.to}
                            className="flex items-center gap-3 px-3 py-2.5 hover:bg-ink-hover transition-colors group">
                        {/* Colour AND a word, always - the detail line names the
                            state in plain English so the dot is reinforcement,
                            not the message. */}
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${tone.dot}`} />
                        <Icon size={14} className="text-text-muted flex-shrink-0" />
                        <span className="min-w-0 flex-1">
                          <span className="text-[13px] text-text block truncate">{a.what}</span>
                          <span className={`text-[11px] ${tone.text}`}>{a.detail}</span>
                        </span>
                        <ArrowRight size={13} className="text-text-dim group-hover:text-accent transition-colors flex-shrink-0" />
                      </Link>
                    );
                  })}
                </div>

                {actions.length > 7 && (
                  <div className="text-[11px] text-text-muted mt-2">
                    and {actions.length - 7} more —{" "}
                    <Link to="/my-compliance" className="text-accent hover:underline">see everything</Link>
                  </div>
                )}
              </div>
            </Card>
          ) : (
            <Card className="mb-6">
              <div className="p-5 flex items-center gap-3">
                <CheckCircle2 size={18} className="text-ok flex-shrink-0" />
                <div>
                  <div className="text-[13px] font-medium text-text">Nothing needs you right now</div>
                  <div className="text-[11.5px] text-text-muted mt-0.5">
                    No tickets expired or expiring, and nothing due on your gear in the next month.
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* ---- My schedule snapshot — today / next / 14-day strip ------- */}
          <div className="mb-6">
            <MyScheduleSnapshot />
          </div>

          {/* ---- Quick actions ------------------------------------------- */}
          {PRIMARY_ACTIONS.some(a => a.roles.includes(user.role)) && (
            <div className="mb-6">
              <SectionLabel>Quick actions</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {PRIMARY_ACTIONS
                  .filter(a => a.roles.includes(user.role))
                  .map(a => <PrimaryAction key={a.to} action={a} />)}
              </div>
            </div>
          )}

          {/* ---- Gear and leave, with something to actually look at ------- */}
          <div className="grid md:grid-cols-2 gap-3 mb-6">
            <Card>
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[13px] font-semibold text-text">My gear</h3>
                  <Link to="/equipment" className="text-[11px] text-accent hover:underline">All equipment</Link>
                </div>
                {gearCount === 0 ? (
                  <p className="text-[12px] text-text-muted">Nothing allocated to you.</p>
                ) : (
                  <div className="space-y-2">
                    <GearRow icon={Truck}       label="Vehicles"      n={gear?.vehicles.length ?? 0} to="/equipment" />
                    <GearRow icon={Wrench}      label="Power tools"   n={gear?.tools.length ?? 0}    to="/inspections" />
                    <GearRow icon={ShieldCheck} label="Rigging gear"  n={gear?.rigging.length ?? 0}  to="/inspections" />
                  </div>
                )}
              </div>
            </Card>

            <Card>
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[13px] font-semibold text-text">This week</h3>
                  <Link to="/timesheets" className="text-[11px] text-accent hover:underline">My timesheet</Link>
                </div>

                <div className="flex items-baseline gap-2">
                  <span className="text-[26px] font-semibold text-text tabular-nums leading-none">
                    {Math.round(weekTotal * 10) / 10}
                  </span>
                  <span className="text-[12px] text-text-muted">hours logged</span>
                </div>

                {/* One series, so no legend - the heading names it. Today is
                    marked so the bars orient without a date axis. */}
                <div className="mt-3">
                  <WeekBars days={weekDays} todayIso={todayIso} />
                </div>

                <div className="text-[11.5px] mt-2.5">
                  {weekTotal === 0
                    ? <span className="text-text-muted">Nothing logged yet this week.</span>
                    : unsubmitted > 0
                      ? <span className="text-warn">
                          {unsubmitted} {unsubmitted === 1 ? "entry" : "entries"} not submitted yet
                        </span>
                      : <span className="text-ok">All submitted</span>}
                </div>
              </div>
            </Card>
          </div>

          {/* ---- The rest of the portal ---------------------------------- */}
          <SectionLabel>Your screens</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {PORTAL_ITEMS
              .filter(item => !item.roles || item.roles.includes(user.role))
              .map(item => <ItemCard key={item.to} item={item} />)}
          </div>

          {/* The key, once, at the bottom - the same words the office uses. */}
          <div className="text-[11px] text-text-dim leading-relaxed mt-6">
            {(["current", "expiring", "expired", "missing"] as ReqStatus[]).map(s => (
              <span key={s} className="mr-4 whitespace-nowrap">
                <span className={`inline-block w-2 h-2 rounded-sm mr-1.5 align-middle ${STATUS_META[s].fill}`} />
                <span className="text-text-muted">{STATUS_META[s].label}</span> — {STATUS_META[s].blurb}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-3">
      <div className="h-1 w-10 bg-accent rounded-full" />
      <div className="text-text-muted text-[11px] tracking-wider2 uppercase font-mono">
        {children}
      </div>
    </div>
  );
}

function GearRow({ icon: Icon, label, n, to }: { icon: LucideIcon; label: string; n: number; to: string }) {
  if (n === 0) return null;
  return (
    <Link to={to} className="flex items-center gap-2.5 group">
      <Icon size={14} className="text-text-muted" />
      <span className="text-[12.5px] text-text flex-1">{label}</span>
      <span className="text-[13px] font-semibold text-text tabular-nums">{n}</span>
      <ArrowRight size={12} className="text-text-dim group-hover:text-accent transition-colors" />
    </Link>
  );
}


// -----------------------------------------------------------------------------
// WeekBars — the shape of somebody's week
// -----------------------------------------------------------------------------
// Scaled to the taller of eight hours or the biggest day, so a normal week fills
// the frame and a big day still fits. Today is outlined rather than recoloured -
// a second colour here would imply a second series.
// -----------------------------------------------------------------------------
function WeekBars({ days, todayIso }: {
  days: { label: string; date: string; hours: number }[];
  todayIso: string;
}) {
  const max = Math.max(8, ...days.map(d => d.hours));
  return (
    <div className="flex items-end gap-1.5">
      {days.map(d => {
        const isToday = d.date === todayIso;
        return (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5">
            <div className={`w-full rounded-sm relative bg-text/[0.06] ${
                  isToday ? "ring-1 ring-accent/40" : ""}`}
                 style={{ height: 44 }}
                 title={`${d.hours ? Math.round(d.hours * 10) / 10 : 0} h`}>
              <div className="absolute bottom-0 left-0 right-0 bg-accent rounded-sm"
                   style={{
                     height: `${(d.hours / max) * 100}%`,
                     transition: "height 600ms cubic-bezier(.2,.8,.2,1)",
                   }} />
            </div>
            <span className={`text-[9px] ${isToday ? "text-accent font-medium" : "text-text-muted"}`}>
              {d.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function PrimaryAction({ action }: { action: PrimaryActionDef }) {
  const Icon = action.icon;
  return (
    <Link to={action.to} className="block group">
      <Card className="h-full border-accent/30 bg-gradient-to-br from-accent/8 to-accent/2 hover:border-accent transition-all">
        <div className="p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-md bg-accent/15 text-accent flex items-center justify-center border border-accent/30 flex-shrink-0">
            <Icon size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-semibold text-text">{action.label}</div>
          </div>
          <ArrowRight size={16} className="text-accent group-hover:translate-x-0.5 transition-transform" />
        </div>
      </Card>
    </Link>
  );
}

function ItemCard({ item }: { item: ItemDef }) {
  const Icon = item.icon;
  return (
    <Link to={item.to} className="block group">
      <Card className="h-full transition-all group-hover:border-accent relative overflow-hidden">
        <div className="absolute top-0 left-0 h-0.5 w-full bg-accent opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded bg-gradient-to-br from-accent/12 to-accent/4 text-accent border border-accent/20 flex items-center justify-center">
              <Icon size={17} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-text">{item.label}</div>
            </div>
            <ArrowRight size={14} className="text-text-muted group-hover:text-accent transition-colors" />
          </div>
          <div className="text-[12px] text-text-muted mt-2">{item.description}</div>
        </div>
      </Card>
    </Link>
  );
}
